import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Link as LinkIcon, Trash2 } from "lucide-react";
import { eurf, fmtLastSeen, STATUS } from "./shared";

export default function OrgRow({ o, token, onSaved, onOpen, onDelete, plans, registerSaver }) {
  const planPrice = (plans || []).find((p) => p.key === o.plan)?.price;
  const [f, setF] = useState({ plan: o.plan, custom_price: o.custom_price ?? (planPrice ?? ""), discount: o.discount ?? 0, commission_pct: o.commission_pct ?? 0, subscription_status: o.subscription_status });
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState("");
  const doConnect = async () => {
    setActing("connect");
    try {
      const { data } = await axios.post(`${API}/superadmin/orgs/${o.id}/connect`, { origin_url: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
      window.open(data.url, "_blank", "noopener");
      toast.success("Lien d'activation Connect ouvert");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setActing(""); }
  };
  const doSubLink = async () => {
    setActing("sub");
    try {
      const { data } = await axios.post(`${API}/superadmin/orgs/${o.id}/subscription-link`, { origin_url: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
      window.open(data.url, "_blank", "noopener");
      toast.success("Lien d'abonnement généré");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setActing(""); }
  };
  const priceChanged = () => {
    const cp = f.custom_price === "" ? null : parseFloat(f.custom_price);
    return (cp ?? planPrice ?? 0) !== (o.custom_price ?? planPrice ?? 0)
      || (parseFloat(f.discount) || 0) !== (o.discount ?? 0)
      || (f.plan || "") !== (o.plan || "");
  };
  const save = async (opts = {}) => {
    const dirty = priceChanged()
      || (parseFloat(f.commission_pct) || 0) !== (o.commission_pct ?? 0)
      || f.subscription_status !== o.subscription_status;
    if (!dirty) return false;
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/orgs/${o.id}/billing`, {
        plan: f.plan, custom_price: f.custom_price === "" ? null : parseFloat(f.custom_price),
        discount: parseFloat(f.discount) || 0, commission_pct: parseFloat(f.commission_pct) || 0,
        subscription_status: f.subscription_status,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (priceChanged()) {
        try {
          await axios.post(`${API}/superadmin/orgs/${o.id}/custom-subscription`, {}, { headers: { Authorization: `Bearer ${token}` } });
          if (!opts.silent) toast.success("Abonnement Revolut créé — email envoyé au prestataire");
        } catch (e) { toast.error(`${o.name} : ${e.response?.data?.detail || "abonnement Revolut non créé"}`); }
      }
      if (!opts.silent) { toast.success("Facturation mise à jour"); onSaved && onSaved(); }
      return true;
    } catch { toast.error(`${o.name} : échec de l'enregistrement`); return false; }
    finally { setSaving(false); }
  };
  useEffect(() => { registerSaver && registerSaver(o.id, () => save({ silent: true })); }); // eslint-disable-line
  return (
    <tr data-testid={`sa-org-${o.id}`} className="border-b border-zinc-800">
      <td className="px-3 py-3">
        <button data-testid={`sa-open-${o.id}`} onClick={() => onOpen && onOpen(o.id)} className="text-left font-medium text-white hover:text-[#FF6A00]">{o.name}</button>
        <p className="text-xs text-zinc-500">{o.admin_email || "—"}</p>
      </td>
      <td className="px-3 py-3 text-sm text-zinc-300">{eurf(o.revenue)}</td>
      <td className="px-3 py-3 text-sm" data-testid={`sa-lastseen-${o.id}`}>
        <span className={fmtLastSeen(o.last_seen).tint}>{fmtLastSeen(o.last_seen).label}</span>
      </td>
      <td className="px-2 py-3">
        <select data-testid={`sa-plan-${o.id}`} value={f.plan || ""} onChange={(e) => { const key = e.target.value; const pl = (plans || []).find((p) => p.key === key); setF((s) => ({ ...s, plan: key, commission_pct: pl ? pl.commission_pct : s.commission_pct })); }} className="h-8 w-28 rounded-md border border-zinc-800 bg-zinc-900 px-2 text-sm text-white">
          <option value="">— Aucun —</option>
          {(plans || []).map((p) => <option key={p.key} value={p.key}>{`${p.label} · ${p.price}€${p.revolut_active ? "" : " (hors Revolut)"}`}</option>)}
        </select>
      </td>
      <td className="px-2 py-3"><Input data-testid={`sa-price-${o.id}`} type="number" value={f.custom_price} onChange={(e) => setF({ ...f, custom_price: e.target.value })} placeholder="Prix €" className="h-8 w-24 border-zinc-800 bg-zinc-900 text-sm" /></td>
      <td className="px-2 py-3"><Input data-testid={`sa-discount-${o.id}`} type="number" value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} placeholder="%" className="h-8 w-20 border-zinc-800 bg-zinc-900 text-sm" /></td>
      <td className="px-2 py-3"><Input data-testid={`sa-commission-${o.id}`} type="number" value={f.commission_pct} onChange={(e) => setF({ ...f, commission_pct: e.target.value })} placeholder="%" title="Rempli automatiquement selon le plan — modifiable manuellement" className="h-8 w-20 border-zinc-800 bg-zinc-900 text-sm" /></td>
      <td className="px-2 py-3">
        <select data-testid={`sa-status-${o.id}`} value={f.subscription_status} onChange={(e) => setF({ ...f, subscription_status: e.target.value })} className="h-8 rounded-md border border-zinc-800 bg-zinc-900 px-2 text-sm text-white">
          {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td className="px-2 py-3">
        <span data-testid={`sa-connect-badge-${o.id}`} className={`text-xs font-medium ${{ active: "text-emerald-400", pending: "text-amber-400" }[o.connect_status] || "text-zinc-500"}`}>
          {{ active: "Actif", pending: "En attente" }[o.connect_status] || "—"}
        </span>
      </td>
      <td className="px-2 py-3">
        <div className="flex items-center gap-1.5">
          <Button data-testid={`sa-connect-${o.id}`} onClick={doConnect} disabled={acting === "connect"} title="Activer / relancer les paiements Connect" variant="outline" className="h-8 rounded-full border-zinc-700 px-2 text-xs text-zinc-200 hover:bg-zinc-800"><CreditCard className="h-3.5 w-3.5" /></Button>
          <Button data-testid={`sa-sublink-${o.id}`} onClick={doSubLink} disabled={acting === "sub"} title="Générer un lien d'abonnement" variant="outline" className="h-8 rounded-full border-zinc-700 px-2 text-xs text-zinc-200 hover:bg-zinc-800"><LinkIcon className="h-3.5 w-3.5" /></Button>
          <Button data-testid={`sa-delete-${o.id}`} onClick={() => onDelete && onDelete(o)} title="Supprimer le prestataire" variant="outline" className="h-8 rounded-full border-red-500/40 px-2 text-xs text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </td>
    </tr>
  );
}
